import { parseISO } from 'date-fns';

// Domain parsing helpers distinguish three semantic categories:
// - CalendarDateString: a calendar date with no time-of-day
// - LocalNaiveDateTimeString: a local wall-clock datetime with no timezone
// - InstantInput: a real instant such as Date, timestamp, or ISO-with-offset
export type CalendarDateString = string;
export type LocalNaiveDateTimeString = string;
export type InstantInput = string | number | Date;
export type DateInput = string | number | Date;

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_NAIVE_DATETIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
const TIMEZONE_OFFSET_PATTERN = /(Z|[+-]\d{2}(?::?\d{2})?)$/;

export function isCalendarDateString(
  value: string,
): value is CalendarDateString {
  return CALENDAR_DATE_PATTERN.test(value);
}

export function isLocalNaiveDateTimeString(
  value: string,
): value is LocalNaiveDateTimeString {
  return LOCAL_NAIVE_DATETIME_PATTERN.test(value);
}

export function parseForStableDisplayDate(date: DateInput) {
  if (typeof date !== 'string') {
    return date;
  }

  if (isCalendarDateString(date)) {
    // Keep date-only strings on the intended calendar day when western
    // timezones render them.
    return parseISO(`${date}T12:00:00.000Z`);
  }

  return parseISO(date);
}

export function toStableDisplayDate(date: DateInput) {
  return parseForStableDisplayDate(date);
}

export function parseCalendarDateForDisplay(date: DateInput) {
  return parseForStableDisplayDate(date);
}

export function toInstantDate(date: DateInput) {
  if (typeof date !== 'string') {
    return date;
  }

  if (isCalendarDateString(date)) {
    return parseForStableDisplayDate(date);
  }

  if (isLocalNaiveDateTimeString(date)) {
    return parseISO(date);
  }

  return parseISO(date);
}

export function parseInstant(date: DateInput) {
  return toInstantDate(date);
}

export function normalizeDateInput(date: DateInput) {
  return toInstantDate(date);
}

function parseTimezoneOffsetMinutes(offsetToken: string) {
  if (offsetToken === 'Z') {
    return 0;
  }

  const sign = offsetToken.startsWith('-') ? -1 : 1;
  const normalizedOffset = offsetToken.slice(1).replace(':', '');
  const hours = parseInt(normalizedOffset.slice(0, 2), 10);
  const minutes =
    normalizedOffset.length > 2
      ? parseInt(normalizedOffset.slice(2, 4), 10)
      : 0;

  return sign * (hours * 60 + minutes);
}

export function getOffsetMinutes(date: string | Date) {
  if (date instanceof Date) {
    return -date.getTimezoneOffset();
  }

  const offsetMatch = TIMEZONE_OFFSET_PATTERN.exec(date);
  return offsetMatch ? parseTimezoneOffsetMinutes(offsetMatch[1]) : 0;
}

function shiftDateByOffset(date: Date, offsetMinutes: number) {
  return new Date(date.getTime() + offsetMinutes * 60000);
}

export function getCalendarDayStartForOffset(
  date: Date,
  offsetMinutes: number,
) {
  const shiftedDate = shiftDateByOffset(date, offsetMinutes);

  return Date.UTC(
    shiftedDate.getUTCFullYear(),
    shiftedDate.getUTCMonth(),
    shiftedDate.getUTCDate(),
  );
}
